import React, { useEffect, useState } from 'react'

import { AnimatePresence, useMotionValue } from 'framer-motion'

import {
  currencyFormatterNoDemcimal,
  currencyFormatterNoTrailingZeros,
} from '../../../../helpers'
import { usePotential } from '../../../PotentialContext'
import { SliderSlopeInterceptType } from '../helpers/calcSliderSlopeIntercept'
import { InvestmentProjection } from '../helpers/types'
import {
  ANIMATION_VARIANTS,
  getBarHeight,
  getBarSnapIndex,
  getBarSnapXPosition,
  getSlopeInterceptIndex,
} from './helpers'
import { ScubberHandle } from './ScubberHandle'
import { GraphSlider, SliderBar } from './styled-components'
import { Tooltip } from './Tooltip'

export const Slider: React.FC<{
  ageOfTransfer: number
  currentAge: number
  barCount: number
  barWidth: number
  graphWidth: number
  graphHeight: number
  projectionRemainZero: boolean
  slopeInterceptList: SliderSlopeInterceptType[]
  yearlyProjection: InvestmentProjection[]
}> = ({
  ageOfTransfer,
  barCount,
  barWidth,
  currentAge,
  graphHeight,
  graphWidth,
  projectionRemainZero,
  slopeInterceptList,
  yearlyProjection,
}) => {
  const { onChange } = usePotential()

  // Subtracting 1 barWidth to account of half of 1st & last barWidth
  const distanceBetweenPeaks = (graphWidth - barWidth) / (barCount - 1)

  const motionXPosition = useMotionValue(0)
  const motionBarHeight = useMotionValue(0)

  const [animationState, setAnimationState] = useState<
    (typeof ANIMATION_VARIANTS)[keyof typeof ANIMATION_VARIANTS] // basically create an union of all the values
  >(ANIMATION_VARIANTS.ENTRY)

  const [snapIndex, setSnapIndex] = useState(ageOfTransfer - currentAge)
  const [snapXPosition, setSnapXPosition] = useState(
    getBarSnapXPosition({
      barCount,
      barWidth,
      graphWidth,
      index: ageOfTransfer - currentAge,
    }),
  )
  const [barSnapHeight, setSnapBarHeight] = useState(0)

  const handleDragStart = () => {
    setAnimationState(ANIMATION_VARIANTS.DRAGGING)
  }

  const handleDragEnd = () => {
    const xPos = motionXPosition.get()

    const snapXIndex = getBarSnapIndex({
      barCount,
      distanceBetweenPeaks,
      xPosition: xPos,
    })
    const snapXPosition = getBarSnapXPosition({
      barCount,
      barWidth,
      index: snapXIndex,
      graphWidth,
    })

    const slopeInterceptIndex = getSlopeInterceptIndex({
      barCount,
      distanceBetweenPeaks,
      xPosition: xPos,
    })
    const { slope, yIntercept } = slopeInterceptList[slopeInterceptIndex]
    const barHeight = getBarHeight({
      slope,
      xPosition: xPos,
      yIntercept,
    })

    setSnapIndex(snapXIndex)
    setSnapXPosition(snapXPosition)
    setSnapBarHeight(barHeight)
    setAnimationState(ANIMATION_VARIANTS.SNAP)
  }

  const handleAnimationComplete = () => {
    switch (animationState) {
      case ANIMATION_VARIANTS.ENTRY:
        handleDragEnd()
        break
      case ANIMATION_VARIANTS.SNAP:
        setAnimationState(ANIMATION_VARIANTS.SNAP)
        break
      default:
        setAnimationState(ANIMATION_VARIANTS.DRAGGING)
        break
    }
  }

  useEffect(() => {
    let unsubscribeX = () => {}

    function updateValues() {
      if (animationState !== ANIMATION_VARIANTS.SNAP) {
        const xPosition = motionXPosition.get()

        const slopeInterceptIndex = getSlopeInterceptIndex({
          barCount,
          distanceBetweenPeaks,
          xPosition,
        })
        const { slope, yIntercept } = slopeInterceptList[slopeInterceptIndex]
        const barHeight = getBarHeight({
          slope,
          xPosition,
          yIntercept,
        })
        motionBarHeight.set(barHeight)

        if (
          animationState === ANIMATION_VARIANTS.DRAGGING ||
          animationState === ANIMATION_VARIANTS.ENTRY
        ) {
          const selectedAge = currentAge + slopeInterceptIndex

          const selectedYearProjection = yearlyProjection[slopeInterceptIndex]

          const projectedValue = currencyFormatterNoDemcimal(
            selectedYearProjection.projectionTotal,
          )

          const netInvestments = currencyFormatterNoTrailingZeros(
            selectedYearProjection.investedTotal,
          )

          const returnValue = currencyFormatterNoTrailingZeros(
            selectedYearProjection.projectionTotal -
              selectedYearProjection.investedTotal,
          )

          onChange({
            netInvestments,
            projectedValue,
            returnValue,
            selectedAge,
          })
        }
      }
    }
    updateValues()
    unsubscribeX = motionXPosition.onChange(updateValues)

    return () => {
      unsubscribeX()
    }
  }, [
    animationState,
    barCount,
    currentAge,
    distanceBetweenPeaks,
    graphHeight,
    motionBarHeight,
    motionXPosition,
    slopeInterceptList,
    yearlyProjection,
    onChange,
  ])

  return (
    <div style={{ position: 'absolute', height: '240px' }}>
      <GraphSlider
        animate={animationState}
        barWidth={barWidth}
        drag="x"
        dragConstraints={{
          left: 0,
          right: graphWidth - barWidth,
        }}
        dragElastic={0}
        dragMomentum={false}
        height={graphHeight}
        initial="initial"
        onAnimationComplete={handleAnimationComplete}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
        style={{
          scale: -1, // inverted on x-axis to y value expand from bottom
          x: motionXPosition,
        }}
        variants={{
          [ANIMATION_VARIANTS.ENTRY]: {
            transition: { ease: 'easeInOut', delay: 0.2, duration: 0.4 },
            x: snapXPosition,
          },
          [ANIMATION_VARIANTS.SNAP]: {
            transition: { ease: 'easeInOut', duration: 0.2 },
            x: snapXPosition,
          },
        }}
      >
        {projectionRemainZero ? null : (
          <>
            <SliderBar
              animate={animationState}
              barWidth={barWidth}
              style={{ height: motionBarHeight }}
              variants={{
                snap: {
                  height: barSnapHeight,
                  transition: { duration: 0.2, ease: 'easeInOut' },
                },
                dragging: {
                  transition: { duration: 0.2, ease: 'easeInOut' },
                },
              }}
            />
            <ScubberHandle
              barWidth={barWidth}
              height={32}
              isDragging={animationState === ANIMATION_VARIANTS.DRAGGING}
              width={49}
            />
            <AnimatePresence>
              {animationState === ANIMATION_VARIANTS.DRAGGING ? null : (
                <Tooltip
                  isAgeOfTransfer={currentAge + snapIndex === ageOfTransfer}
                  isDragging
                  projectionTotal={yearlyProjection[snapIndex].projectionTotal}
                  snapIndex={snapIndex}
                />
              )}
            </AnimatePresence>
          </>
        )}
      </GraphSlider>
    </div>
  )
}
